import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseLanguageModelInput } from '@langchain/core/language_models/base';
import { ChatOpenAIFields } from '@langchain/openai';
import { BotidService } from '@shared-angular/services/ragai/botid.service';
import { GTPChatModelType } from '@shared/index';
import { aiTextOpenaiSchema } from '@shared/ragai.schemas';
import { firstValueFrom } from 'rxjs';
import { z } from 'zod';
import { EventManagerService } from '../../event-manager/event-manager.service';

/** Chat-Completions und Bild-Nachrichteninhalte per OpenAI (`RAGAI.AITEXT_OPENAI`). */
/**
 * Chat-Completions und Bild-Nachrichteninhalte per OpenAI (`RAGAI.AITEXT_OPENAI`).
 * @remarks
 * {@link chatCompletion}/{@link chatCompletionJson} und {@link VllmService.chatCompletion} rufen intern denselben
 * Backend-Endpunkt-Namensraum auf (`/api/toolbox/completion/create/<openai|vllm>/<botId>`) - Unterschied ist nur
 * das Backend-Modell (OpenAI-Cloud vs. selbstgehostetes vLLM) und die verfügbaren Modell-/Parameter-Optionen.
 * {@link structuredResponse} nutzt einen separaten Endpunkt (`/api/toolbox/responses/create`) und erzwingt eine
 * gegen ein JSON-Schema validierte Antwort - nützlich, wenn die KI-Antwort direkt als typisiertes Objekt statt
 * als Freitext weiterverarbeitet werden soll.
 */
@Injectable({
  providedIn: 'root'
})
export class OpenaiService {

  constructor(
    private eventManager: EventManagerService,
    private http: HttpClient,
    private botIdService: BotidService) {
  }

  private async _complexCompletion(modelConf: ChatOpenAIFields, promptInput: BaseLanguageModelInput, llmType: string, jsonResponse: boolean, webSearch: boolean = false) {
    this.eventManager.triggerEventHook("io_out_chatcompletion_start", { modelConf, promptInput, llmType, jsonResponse, webSearch });
    const result = await firstValueFrom(this.http.post("/api/toolbox/completion/create/" + llmType + "/" + this.botIdService.getBotId(),
      {
        modelConf,
        promptInput,
        jsonResponse,
        webSearch
      }
    ));
    this.eventManager.triggerEventHook("io_in_chatcompletion_end", { modelConf, promptInput, llmType, jsonResponse, webSearch, result });
    return result;
  }

  /**
   * Baut einen Bild-Nachrichteninhalt für die Chat-Completion-API.
   * @param src Bild-URL oder Skill-ID; Skill-IDs werden zu einer ladbaren URL aufgelöst.
   * @returns Der Nachrichteninhalt im OpenAI-Bildformat.
   */
  public async getImageMesageContent(src: string) {
    if (!src.startsWith('http')) {
      src = await window.RAGAI.HELPER.getImageURlFromSkill(src);
    }

    return {
      type: "image_url",
      image_url:
      {
        url: src,
        detail: "auto"
      }
    };
  }



  /**
   * Sendet eine Chat-Completion-Anfrage an OpenAI und liefert die Text-Antwort.
   * @param input Der Prompt-Text oder ein Objekt gemäß `aiTextOpenaiSchema`.
   * @param model Das zu verwendende OpenAI-Modell.
   * @param temperature Kreativität der Antwort (0 = deterministisch, höher = kreativer).
   * @param webSearch Aktiviert die Websuche für diese Anfrage.
   * @returns Die Antwort des Modells.
   */
  public async chatCompletion(input: string | z.input<typeof aiTextOpenaiSchema>, model: GTPChatModelType = "gpt-4.1", temperature: number = .7, webSearch: boolean = false) {
    const parsed = aiTextOpenaiSchema.safeParse(input);
    if (parsed.success && typeof input === 'object') {
      input = parsed.data.input;
      model = (parsed.data.model ?? 'gpt-4.1') as GTPChatModelType;
      temperature = parsed.data.temperature ?? .7;
    }
    const modelConf: ChatOpenAIFields = {
      model,
      temperature
    };
    const promptInput: BaseLanguageModelInput = input as string;
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.AITEXT_OPENAI.CHATCOMPLETION.request", { request: { modelConf, promptInput, webSearch } });
    const result = await this._complexCompletion(modelConf, promptInput, "openai", false, webSearch);
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.AITEXT_OPENAI.CHATCOMPLETION.result", { result, request: { modelConf, promptInput, webSearch } });
    return result;
  }


  /**
   * Wie {@link chatCompletion}, fordert aber eine JSON-Antwort vom Modell an.
   * @param input Der Prompt-Text oder ein Objekt gemäß `aiTextOpenaiSchema`.
   * @param model Das zu verwendende OpenAI-Modell.
   * @param temperature Kreativität der Antwort (0 = deterministisch, höher = kreativer).
   * @param webSearch Aktiviert die Websuche für diese Anfrage.
   * @returns Die Antwort des Modells als JSON-String.
   */
  public async chatCompletionJson(input: string | z.input<typeof aiTextOpenaiSchema>, model: GTPChatModelType = "gpt-4.1", temperature: number = .7, webSearch: boolean = false) {
    const parsed = aiTextOpenaiSchema.safeParse(input);
    if (parsed.success && typeof input === 'object') {
      input = parsed.data.input;
      model = (parsed.data.model ?? 'gpt-4.1') as GTPChatModelType;
      temperature = parsed.data.temperature ?? .7;
    }
    const modelConf: ChatOpenAIFields = {
      model,
      temperature
    };
    const promptInput: BaseLanguageModelInput = input as string;
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.AITEXT_OPENAI.CHATCOMPLETION.request", { request: { modelConf, promptInput, webSearch } });
    const result = await this._complexCompletion(modelConf, promptInput, "openai", true, webSearch);
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.AITEXT_OPENAI.CHATCOMPLETION.result", { result, request: { modelConf, promptInput, webSearch } });
    return result;
  }

  /**
   * Sendet eine Chat-Completion-Anfrage an OpenAI und validiert die Antwort gegen ein JSON-Schema.
   * @param options Optionen für die strukturierte Anfrage.
   * @param options.promptInput Der Prompt-Text.
   * @param options.jsonSchema Das JSON-Schema, gegen das die Antwort validiert wird.
   * @param options.jsonSchema.name Name des JSON-Schemas.
   * @param options.jsonSchema.schema Das JSON-Schema selbst.
   * @param options.model Das zu verwendende OpenAI-Modell.
   * @param options.temperature Kreativität der Antwort.
   * @param options.instructions Zusätzliche System-Anweisungen.
   * @param options.webSearch Aktiviert die Websuche für diese Anfrage.
   * @returns Die strukturierte, typisierte Antwort.
   */
  public async structuredResponse<T = unknown>(options: {
    promptInput: string;
    jsonSchema: { name: string; schema: Record<string, unknown> };
    model?: GTPChatModelType;
    temperature?: number;
    instructions?: string;
    webSearch?: boolean;
  }): Promise<T> {
    const {
      promptInput,
      jsonSchema,
      model = "gpt-4o-mini",
      temperature = 0.2,
      instructions,
      webSearch = false
    } = options;
    const modelConf: ChatOpenAIFields = { model, temperature };
    const body = { modelConf, promptInput, jsonSchema, webSearch, ...(instructions != null ? { instructions } : {}) };
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.AITEXT_OPENAI.STRUCTURED_RESPONSE.request", { request: body });
    const result = await firstValueFrom(
      this.http.post<T>("/api/toolbox/responses/create/" + this.botIdService.getBotId(), body)
    );
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.AITEXT_OPENAI.STRUCTURED_RESPONSE.result", { result, request: body });
    return result;
  }
}
